import type { Joint } from "@/shared/state/visualization/schema/v1/three-d";

/** The pose a model is exported in, and the value every joint is measured from. */
const REST = 0;

/**
 * The value a joint sits at until something else poses it: the value in the
 * joint's reachable range closest to the robot's rest pose.
 *
 * Rest is zero — the pose a `ROBOTO_device` glTF is exported at, and the pose
 * every joint value is measured from. A joint whose limits **contain** zero is
 * therefore already at rest and stays there. A joint whose limits **exclude**
 * zero is the case this exists for: `[0.1, 0.5]` cannot physically be at zero,
 * so seeding it there shows the robot in a configuration it cannot adopt, and
 * the honest starting value is the endpoint nearest rest — `0.1` here, `-0.1`
 * for `[-0.5, -0.1]`. Arithmetically that is a clamp of zero into the range;
 * what it *means* is "as close to rest as this joint can actually get".
 *
 * | Joint | Value |
 * |---|---|
 * | limits contain zero (`[-1, 1]`, `[-0.3, 0]`) | `0` |
 * | limits exclude zero (`[0.1, 0.5]`) | the nearer endpoint, `0.1` |
 * | `lower == upper` — pinned | that one value |
 * | no limits declared | `0` — nothing constrains it |
 * | `continuous` | `0` — it has no limits by definition, and any it carries do not bound it |
 * | `fixed`, `floating`, `planar` | none: they are not driven by a value |
 * | coupled by `mimic` | none: see below |
 * | `lower > upper` | none: the range is malformed, so no value in it can be trusted |
 *
 * Units follow the joint: radians for `revolute`/`continuous`, metres for
 * `prismatic`. The rule is the same either way — a gripper finger limited to
 * `[0.01, 0.04]` m opens to 1cm rather than pretending to close to zero.
 *
 * `safety.softLower`/`softUpper` play no part in this, and the extension is
 * explicit about why: they are a *controller's* soft stops, tighter than the
 * kinematic range, and must not silently replace `limit.lower`/`upper`. Only
 * `limit` reaches this function — see `articulation.ts`, which reads nothing
 * else.
 *
 * ## Coupled (`mimic`) joints have no default of their own
 *
 * A coupled joint's value is `multiplier * driver + offset` — a function of
 * another joint, not an independent choice. Choosing a value for it
 * independently would be the one thing worse than leaving it alone: a parallel
 * gripper whose two fingers were seeded separately sits in a pose the mechanism
 * forbids. So it gets `undefined` — no value of its own — and its actual value
 * comes from evaluating the coupling against its driver, which is
 * `evaluateJointValues`' job rather than this function's.
 *
 * @returns the joint's default value, or `undefined` if it has no value of its
 *   own — which is also the test for whether anything may pose it at all
 */
export function restingJointValue(joint: Joint): number | undefined {
  if (joint.mimic !== undefined) {
    return undefined;
  }

  switch (joint.type) {
    case "fixed":
    case "floating":
    case "planar":
      // Not driven by a single value: nothing at all for `fixed`, and 6 and 3
      // degrees of freedom for `floating` and `planar`.
      return undefined;
    case "continuous":
      // Turns without end, so limits — if the file carries any — do not bound
      // it and there is nothing to constrain zero into.
      return REST;
    case "revolute":
    case "prismatic":
      break;
  }

  const { lower, upper } = joint;
  if (lower === undefined || upper === undefined) {
    return REST;
  }
  if (lower > upper) {
    return undefined;
  }
  // `lower === upper` is a pinned joint rather than a malformed one — under URDF
  // 1.0/1.1 a bare `<limit effort= velocity=>` means exactly that — and falls
  // out of this without a case of its own: the range holds one value, so that is
  // the value nearest rest.
  return Math.min(Math.max(REST, lower), upper);
}
